import React, { forwardRef } from "react";
import { useTranslation, type WithTranslation } from "react-i18next";
import { Stack, type SxProps } from "~/common/mui";
import Chip from "~/common/components/Chip/Chip";
import { ReverseParam } from "~/common/components/Parameter";
import { DateFormatType, formatNullableDate } from "~/common/utils/DateFormatter";
import { Price } from "~/domains/pricing/components/Price/v3/Price";
import { IncludedSurcharge } from "~/domains/product/components/ProductParams";
import type { NetPriceData, TotalPriceData, WithSurcharge } from "~/domains/pricing/types/PriceData";
import { getTotalPrice } from "~/domains/product/utils/productDescriptorUtils";
import { volatileSum } from "~/utils/PriceUtils";
import { uniqueValues } from "~/common/utils/misc";
import type { QuoteLineMO } from "~/features/orders/common/components/v2/models";
import type { RenderCboxFn } from "~/features/orders/common/components/v2/QuoteTable/types";
import { getEDD } from "~/features/orders/common/components/v2/QuoteTable/utils";
import { QuoteRow, QuoteCell, RowDivider } from "./styled";
import { styled } from "~/common/mui/styled/styled";

// ============================================================================
// Styled Components
// ============================================================================

const PartsContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "subgrid",
  gridColumn: "1 / -1",
  rowGap: theme.spacing(1),
}));

// ============================================================================
// Types
// ============================================================================

interface Props {
  quoteLine: QuoteLineMO;
  variant?: "main" | "alternative";
  forceRender?: boolean;
  hidden?: boolean;
  renderCheckbox?: RenderCboxFn;
  sx?: SxProps;
}

interface SumPartsRow {
  key: string;
  title: React.ReactNode;
  confirmedQuantity: number;
  requestedQuantity: number;
  estimatedDeliveryDate: string;
  requestedDeliveryDate: string;
  price?: NetPriceData;
  totalPrice: TotalPriceData & WithSurcharge;
  status?: React.ReactNode;
}

// ============================================================================
// Component
// ============================================================================

export const QuotePartsTable = forwardRef<HTMLDivElement, Props>(
  ({ quoteLine, variant = "main", forceRender, renderCheckbox, hidden, sx }, ref) => {
    const { t } = useTranslation();

    if (!forceRender && variant === "main" && quoteLine.parts.length < 2) {
      return null;
    }

    if (hidden) {
      return null;
    }

    const { header, parts } = sumParts({ quoteLine, variant, t });

    return (
      <PartsContainer ref={ref} sx={sx}>
        {/* Header Row */}
        <QuoteRow>
          <QuoteCell>{header.title}</QuoteCell>
          <QuoteCell>
            <ReverseParam label={header.confirmedQuantity}>{header.requestedQuantity}</ReverseParam>
          </QuoteCell>
          <QuoteCell />
          <QuoteCell>
            <ReverseParam label={header.estimatedDeliveryDate}>{header.requestedDeliveryDate}</ReverseParam>
          </QuoteCell>
          <QuoteCell />
          <QuoteCell>
            {header.price && (
              <ReverseParam
                label={<Price price={header.price} zeroText={t("product.priceOnRequest")} surchargeEnabled />}
              >
                <Price.NetPriceUnit price={header.price} variant="short" surchargeEnabled />
              </ReverseParam>
            )}
          </QuoteCell>
          <QuoteCell>
            <Stack gap={2}>
              <ReverseParam label={<Price as="b" price={header.totalPrice} surchargeEnabled />}>
                <Price.TotalPriceUnit price={header.totalPrice} variant="short" surchargeEnabled={false} />
              </ReverseParam>
              <IncludedSurcharge calculateFrom={header.totalPrice} />
            </Stack>
          </QuoteCell>
          <QuoteCell>{header.status}</QuoteCell>
          <QuoteCell isCheckbox>{renderCheckbox?.(quoteLine)}</QuoteCell>
        </QuoteRow>
        <RowDivider sx={{ borderBottomWidth: 3 }} />

        {/* Part Rows */}
        {parts.map((part, index) => (
          <React.Fragment key={part.key}>
            <QuoteRow>
              <QuoteCell>{part.title}</QuoteCell>
              <QuoteCell>
                <ReverseParam label={part.confirmedQuantity}>{part.requestedQuantity}</ReverseParam>
              </QuoteCell>
              <QuoteCell />
              <QuoteCell>
                <ReverseParam label={part.estimatedDeliveryDate}>{part.requestedDeliveryDate}</ReverseParam>
              </QuoteCell>
              <QuoteCell />
              <QuoteCell>
                {part.price && (
                  <ReverseParam
                    label={<Price price={part.price} zeroText={t("product.priceOnRequest")} surchargeEnabled />}
                  >
                    <Price.NetPriceUnit price={part.price} variant="short" surchargeEnabled />
                  </ReverseParam>
                )}
              </QuoteCell>
              <QuoteCell>
                <Stack gap={2}>
                  <ReverseParam label={<Price as="b" price={part.totalPrice} surchargeEnabled />}>
                    <Price.TotalPriceUnit price={part.totalPrice} variant="short" surchargeEnabled={false} />
                  </ReverseParam>
                  <IncludedSurcharge calculateFrom={part.totalPrice} />
                </Stack>
              </QuoteCell>
              <QuoteCell />
              <QuoteCell />
            </QuoteRow>
            <RowDivider />
          </React.Fragment>
        ))}
      </PartsContainer>
    );
  },
);

QuotePartsTable.displayName = "QuotePartsTable";

// ============================================================================
// Helper Functions
// ============================================================================

export function sumParts({
  quoteLine,
  variant,
  t,
}: Props & {
  t: WithTranslation["t"];
}) {
  const header: Partial<SumPartsRow> = {
    key: "header",
  };

  if (variant === "main") {
    header.title = <strong>Main quote line</strong>;
  } else {
    header.title = `Alternative to quote line ${quoteLine.alternativeTo}`;
  }

  header.confirmedQuantity = quoteLine.parts.reduce((acc, part) => acc + part.confirmedQuantity, 0);
  header.requestedQuantity = quoteLine.parts[0].requestedQuantity;

  const edds = uniqueValues(quoteLine.parts.map(getEDD));
  header.estimatedDeliveryDate = edds[0] + (edds.length > 1 ? ", ..." : "");
  header.requestedDeliveryDate = formatNullableDate(quoteLine.parts[0].requestedDeliveryDate, DateFormatType.DATE);

  const parts = quoteLine.parts.map((part) => ({
    key: part.lineNo,
    title: `${quoteLine.lineNo}:${part.lineNo}`,
    confirmedQuantity: part.confirmedQuantity,
    requestedQuantity: part.requestedQuantity,
    estimatedDeliveryDate: getEDD(part),
    requestedDeliveryDate: formatNullableDate(part.requestedDeliveryDate, DateFormatType.DATE),
    price: part.price,
    totalPrice: getTotalPrice(part.price, part.confirmedQuantity),
  }));

  if (parts.length === 1) {
    header.price = parts[0].price;
    header.totalPrice = parts[0].totalPrice;
  } else {
    const sumResult = volatileSum(...parts.map((part) => part.totalPrice));
    header.totalPrice = {
      ...sumResult,
      baseValue: sumResult.value,
      surcharge: 0,
      isSubscription: parts[0].price?.isSubscription ?? false,
    };
  }

  header.status = <Chip>{t(`codes.quoteLineItemStatus.${quoteLine.status}`)}</Chip>;

  return {
    header,
    parts: parts.length > 1 ? parts : [],
  } as unknown as {
    header: SumPartsRow;
    parts: SumPartsRow[];
  };
}
